/**
 * TanStack Query hooks and key factory for study operations in the internal experience.
 */

import { useInfiniteQuery } from "@tanstack/react-query"
import { daeApiService } from "@/services/daeApi"

export const studyKeys = {
  all: ["studies"] as const,
  lists: () => [...studyKeys.all, "list"] as const,
  list: (daeId: string | undefined) => [...studyKeys.lists(), daeId ?? null] as const,
  details: () => [...studyKeys.all, "detail"] as const,
  detail: (studyId: string | undefined) => [...studyKeys.details(), studyId ?? null] as const,
}

/**
 * Fetches paginated studies the caller can see (granted, or all with a tenant set). Uses infinite
 * query to progressively load all pages from the backend, enabling client-side pagination over the
 * accumulated collection.
 */
export function useStudiesList(daeId: string | undefined, pageSize = 20) {
  return useInfiniteQuery({
    queryKey: studyKeys.list(daeId),
    queryFn: ({ pageParam }) =>
      daeApiService.getDAEStudies({
        limit: pageSize,
        nextToken: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? null,
    enabled: !!daeId,
  })
}
